import { sanitizeURL } from '../shared/utils/sanitize';
import {
  fetchChatResponse,
  fetchTextHandoffStatus,
  notifyTextHandoffPresence,
  requestTextHandoff,
  resolveApiBaseUrl,
} from './api';
import {
  appendInlineCalendlyWidget,
  collapseWidget,
  detectBookingIntent,
  restoreChatMessages,
  showCalendlyLoadingMessage,
} from './booking-helpers';
import {
  CalendlyEventData,
  fetchWidgetConfig,
  initCalendlyInline,
  loadCalendlyScript,
  logMeetingBooking,
} from './calendly';
import {
  appendHandoffNotification,
  getPrimaryMessageWrapper,
  recieveAndAppendMessage,
  sendAndAppendMessage,
  showDots,
} from './message-helpers';
import { templateTextWidget } from './template';
import { clearHandoffId, createToken, getActiveHandoffSessionToken, getStoredHandoffId, resetToken, storeActiveHandoffSession } from './token';
import { MainWindow, Nullable, TextChatUIElements } from './types';

const widget = document.createElement('template');
widget.innerHTML = templateTextWidget;

class WitzoChatElement extends HTMLElement {
  /**Url to connect to the webhook */
  url: Nullable<string> = null;
  /**Reference for elements */
  chat: Nullable<TextChatUIElements> = null;

  default_primary_text: Nullable<string> = null;

  bot_logo_color: Nullable<string> = ``;

  user_chat_color: Nullable<string> = ``;

  send_button_color: Nullable<string> = ``;

  floating_button_color: Nullable<string> = ``;

  uniqueId: Nullable<string> = null;
  apiBaseUrl: Nullable<string> = null;

  banner_TextParagraph: Nullable<string> = null;

  banner_TextParagraphColor: Nullable<string> = null;

  /**Calendly URL for meeting booking */
  private calendlyUrl: Nullable<string> = null;
  private calendlyInitialized: boolean = false;
  private calendlyListenerAttached: boolean = false;
  private loggedEventUris: Set<string> = new Set();

  /**Session ID for tracking */
  private sessionId: string = '';
  private latestRequestId: number = 0;
  private latestUserMessageId: number = 0;
  private feedbackTimer: ReturnType<typeof setTimeout> | null = null;
  private greetingNudgeTimer: ReturnType<typeof setTimeout> | null = null;
  private handoffPollTimer: ReturnType<typeof setTimeout> | null = null;
  private handoffHeartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private handoffInactiveTimer: ReturnType<typeof setTimeout> | null = null;
  private handoffEventSource: EventSource | null = null;
  private handoffWaitingWrapper: HTMLDivElement | null = null;
  private handoffWaitingTimer: ReturnType<typeof setTimeout> | null = null;
  private handoffAwaitingAgentReply: boolean = false;
  private handoffRestoring: boolean = false;
  private feedbackSubmitted: boolean = false;
  private handoffEnabled: boolean = false;
  private handoffStatus: 'waiting_for_agent' | 'assigned' | 'closed' | null = null;
  private handoffAssignedNotified: boolean = false;
  private handoffMarkedLeft: boolean = false;
  private seenHandoffMessageIds: Set<string> = new Set();
  /** Slow poll used only while watching for an agent takeover (SSE fallback). */
  private handoffWatchTimer: ReturnType<typeof setTimeout> | null = null;
  /** Gates the takeover watch: no chat opened means nothing to take over. */
  private hasOpenedWidget: boolean = false;
  private static FEEDBACK_IDLE_MS = 45_000;
  private static GREETING_NUDGE_MS = 15_000;
  private static HANDOFF_POLL_MS = 1500;
  // Deliberately slow: this runs for every visitor with no handoff in progress,
  // and only needs to catch an agent takeover, which is rare. SSE does the
  // real-time work; this is just the fallback when EventSource is unavailable.
  private static HANDOFF_WATCH_POLL_MS = 20_000;
  private static HANDOFF_HEARTBEAT_MS = 30_000;
  private static HANDOFF_INACTIVE_MS = 180_000;

  /**Is Initialized */
  isInitialized: boolean = false;
  private _mainWindow: MainWindow;

  private successfulChatCount: number;
  private date: Date;
  private flags: {
    isOpen: boolean;
    isButtonClicked: boolean;
    isAutoOpen: boolean;
    defaultMessageDisplayed: boolean;
  } = {
    isButtonClicked: false,
    isOpen: false,
    isAutoOpen: false,
    defaultMessageDisplayed: false,
  };

  private readonly handoffPresenceHandler = () => {
    void this.markHandoffVisitorLeft('page_unload', true);
  };
  private readonly handoffVisibilityHandler = () => {
    if (document.visibilityState === 'hidden') {
      void this.markHandoffVisitorLeft('page_hidden', true);
    } else {
      void this.markHandoffVisitorActive('page_visible');
    }
  };
  private readonly handoffOnlineHandler = () => {
    void this.markHandoffVisitorActive('network_online');
  };
  private readonly handoffOfflineHandler = () => {
    void this.markHandoffVisitorLeft('network_offline', true);
  };
  private readonly handoffActivityHandler = () => {
    if (!this.handoffStatus) return;
    this.resetHandoffInactivityTimer();
    if (this.handoffMarkedLeft && document.visibilityState !== 'hidden') {
      void this.markHandoffVisitorActive('user_activity');
    }
  };
  private readonly activeHandoffSessionHandler = () => {
    const activeSession = getActiveHandoffSessionToken();
    if (!activeSession || activeSession === this.sessionId || this.handoffStatus) return;
    this.sessionId = activeSession;
    void this.restoreExistingHandoff();
  };

  constructor() {
    super();
    const node = widget.content.cloneNode(true);
    //Assume no outside influence on the styling should be possible
    const root = this.attachShadow({ mode: 'open' });
    root.appendChild(node);

    this._mainWindow = {
      back: () => {},
      close: () => {},
    };

    this.getElements(root);
    this.successfulChatCount = 0;
    this.date = new Date();

    const date = sessionStorage.getItem('witzo_chat_date');
    if (date) {
      this.date = new Date(date);
    } else {
      sessionStorage.setItem('witzo_chat_date', new Date().toISOString());
    }

    const chatCount = sessionStorage.getItem('witzo_chat_count');
    if (chatCount) this.successfulChatCount = Number(chatCount);
    else sessionStorage.setItem('witzo_chat_count', `0`);

    // Initialize as null instead of empty string
    this.default_primary_text = null;
    this.bot_logo_color = ``;
    this.send_button_color = '';
    this.floating_button_color = '';
  }

  /**
   * @description retrieve elements from shadow dom and ensures initialization
   */
  private getElements(root: ShadowRoot) {
    const query = <T extends HTMLElement>(selctor: string) => root.querySelector(selctor) as T;
    try {
      this.chat = {
        // backButton: query('#backToSelection'),
        bannerText: query('#banner-text'),
        logoIcon: query('#logoIcon'),
        closeIcon: query('#close-button-icon'),
        chatHeader: query('#chat-header'),
        chatWindow: query('#textChatWidget'),
        closeButton: query('#closeTextChat'),
        sendButton: query('#textSendButton'),
        messageInput: query('#textMessageInput'),
        messagesContainer: query('#textMessagesArea'),
        sendButtonRect: root.querySelector('#send-button-color') as SVGRectElement,
        floatingButton: query('#floating-btn'),
        bannerTextParagraph: query('#banner-text-paragraph'),
        calendlyButton: query('#calendlyButton'),
        calendlyButtonWrap: query('#calendlyButtonWrap'),
        handoffButton: query('#handoffButton'),
        handoffButtonWrap: query('#handoffButtonWrap'),
        calendlyContainer: query('#calendlyContainer'),
        calendlyCloseButton: query('#calendlyCloseButton'),
        calendlyWidgetWrapper: query('#calendlyWidgetWrapper'),
        feedbackBar: query('#feedbackBar'),
        feedbackLikeBtn: query('#feedbackLikeBtn'),
        feedbackDislikeBtn: query('#feedbackDislikeBtn'),
      };
      this.isInitialized = true;
    } catch (err) {
      this.isInitialized = false;
      console.warn(`Couldn't initialize ${err}`);
    }
  }

  /**
   * @description Parse markdown text to HTML
   */
  private parseMarkdownToHTML(text: string): string {
    if (!text || text.trim() === '') {
      console.warn('Empty text passed to parseMarkdownToHTML');
      return '';
    }

    // Normalize newlines - handle multiple formats:
    // HTML attributes store \n as literal two-character string (backslash + n)
    // We need to convert these to actual newline characters
    let normalizedText = text;

    // First, handle the literal string \n (from HTML attributes)
    // In JavaScript regex, we need to match a backslash followed by 'n'
    // A backslash in regex is \\, so \\n matches literal \n
    normalizedText = normalizedText.replace(/\\n/g, '\n');

    // Also handle \r\n (Windows line endings)
    normalizedText = normalizedText.replace(/\r\n/g, '\n');

    // Collapse excessive blank lines (3 or more newlines become 2)
    normalizedText = normalizedText.replace(/\n{3,}/g, '\n\n');

    const lines = normalizedText.split('\n');

    const out: string[] = [];
    let inUL = false;
    let inOL = false;

    const flushLists = () => {
      if (inUL) {
        out.push('</ul>');
        inUL = false;
      }
      if (inOL) {
        out.push('</ol>');
        inOL = false;
      }
    };

    const formatInline = (s: string) => {
      // links [text](url) with URL sanitization
      s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_match, linkText, url) => {
        const safeUrl = sanitizeURL(url);
        return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer">${linkText}</a>`;
      });
      // **bold**
      s = s.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      return s;
    };

    for (let raw of lines) {
      const line = raw.trim();

      // Blank line -> close any open list and add a small spacer
      if (line === '') {
        flushLists();
        continue;
      }

      // Check for heading-style lines (lines that are entirely bold)
      // Match lines like "** What I Can Help With..**" or "**Note:**"
      const headingMatch = line.match(/^\*\*\s*(.+?)\s*:?\s*\.{0,2}\*\*$/);
      if (headingMatch) {
        flushLists();
        out.push(`<p><strong>${headingMatch[1]}</strong></p>`);
        continue;
      }

      // Numbered list "1. item"
      const olMatch = line.match(/^(\d+)\.\s+(.*)$/);
      if (olMatch) {
        const item = formatInline(olMatch[2]);
        if (inUL) {
          out.push('</ul>');
          inUL = false;
        }
        if (!inOL) {
          out.push('<ol>');
          inOL = true;
        }
        out.push(`<li>${item}</li>`);
        continue;
      }

      // Bulleted list "- item"
      const ulMatch = line.match(/^-\s+(.*)$/);
      if (ulMatch) {
        const item = formatInline(ulMatch[1]);
        if (inOL) {
          out.push('</ol>');
          inOL = false;
        }
        if (!inUL) {
          out.push('<ul>');
          inUL = true;
        }
        out.push(`<li>${item}</li>`);
        continue;
      }

      // Standalone label lines ending with ":" that aren't already bold headings
      // Match lines like "Note:" or "Tip:" but NOT "**Note:**" (already handled above)
      const labelMatch = line.match(/^(?!\*\*)(.+?):\s*$/);
      if (labelMatch && !line.includes('**')) {
        flushLists();
        // preserve any inline **bold** within the label too
        const labelText = formatInline(labelMatch[1]);
        out.push(`<p><strong>${labelText}:</strong></p>`);
        continue;
      }

      // Normal paragraph
      flushLists();
      out.push(`<p>${formatInline(line)}</p>`);
    }

    flushLists();

    // Final tidy-up: remove accidental double breaks or empty paragraphs
    return out
      .join('')
      .replace(/(<br>\s*){2,}/g, '<br>')
      .replace(/<p>\s*<\/p>/g, '');
  }

  /**
   * @description Process and display the default primary message
   */
  private processAndDisplayMessage() {
    // Prevent duplicate display
    if (this.flags.defaultMessageDisplayed) {
      return;
    }

    if (!this.chat?.messagesContainer) {
      console.warn('Messages container not available');
      return;
    }

    if (!this.default_primary_text || this.default_primary_text.trim() === '') {
      console.warn('No default primary text to process');
      return;
    }

    const wrapper = getPrimaryMessageWrapper(
      this.chat.messagesContainer,
      this.bot_logo_color ?? '#fc0e3f'
    );

    const formattedContent = this.parseMarkdownToHTML(this.default_primary_text);
    const htmlWrapped = `<div class="md-content">${formattedContent}</div>`;

    const getText = async () => ({
      content: htmlWrapped,
      isHtml: true, // make sure your renderer uses innerHTML when this is true
    });
    recieveAndAppendMessage(wrapper, this.chat!.messagesContainer, getText());

    // Mark as displayed to prevent duplicates
    this.flags.defaultMessageDisplayed = true;
  }

  /**
   * @description external function to set the url to connect to web-hook
   */
  set chatUrl(url: string) {
    this.url = url;
    if (this.isConnected) {
      void this.initializeCalendly();
    }
  }

  set mainWindow(_mainWindow: MainWindow) {
    this._mainWindow = _mainWindow;
  }

  set defaultPrimaryText(text: string) {
    this.default_primary_text = text;

    // If elements aren't ready yet, the text will be processed in connectedCallback
    if (!this.isInitialized || !this.chat?.messagesContainer) {
      return;
    }

    this.processAndDisplayMessage();
  }

  set botBackgroundColor(color: string) {
    this.bot_logo_color = color;
  }

  set sendBackgroundColor(color: string) {
    this.send_button_color = color;
    if (this.chat) this.chat.sendButtonRect.style.fill = color;
  }

  set floatingButtonColor(color: string) {
    this.floating_button_color = color;
    if (this.chat?.floatingButton) this.chat.floatingButton.style.backgroundColor = color;
  }

  set bannerText(text: string) {
    if (this.chat?.bannerText) this.chat.bannerText.textContent = text;
  }

  set chatHeader(backgroundColor: string) {
    if (this.chat?.chatHeader) this.chat.chatHeader.style.backgroundColor = backgroundColor;
    if (this.chat?.closeButton) this.chat.closeButton.style.backgroundColor = backgroundColor;
  }

  set logoIcon(iconUrl: string) {
    if (this.chat?.logoIcon) this.chat.logoIcon.src = iconUrl;
  }

  set autoOpen(isAutoOpen: boolean) {
    this.flags.isAutoOpen = isAutoOpen;
  }

  set bannerTextColor(colorValue: string) {
    if (this.chat?.bannerText) this.chat.bannerText.style.color = colorValue;
  }

  set closeButtonColor(colorValue: string) {
    if (this.chat?.closeIcon) this.chat.closeIcon.style.fill = colorValue;
  }

  set userChatColor(colorValue: string) {
    this.user_chat_color = colorValue;
  }

  set uniqueID(uniqueId: string) {
    this.uniqueId = uniqueId;
    if (this.isConnected) {
      void this.initializeCalendly();
    }
  }

  set apiBaseURL(url: string) {
    this.apiBaseUrl = url;
    if (this.isConnected) {
      void this.initializeCalendly();
    }
  }

  private getResolvedApiBaseUrl() {
    return resolveApiBaseUrl(this.url ?? '', this.apiBaseUrl);
  }

  set bannerTextParagraph(text: string) {
    if (this.chat?.bannerTextParagraph) this.chat.bannerTextParagraph.textContent = text;
  }

  set bannerTextParagraphColor(colorValue: string) {
    if (this.chat?.bannerTextParagraph) this.chat.bannerTextParagraph.style.color = colorValue;
  }

  async handleResponse(text: string) {
    if (!text || !this.url) return;
    if (!this.chat) return;

    sendAndAppendMessage(
      this.chat.messagesContainer,
      this.chat.messageInput,
      text,
      this.user_chat_color ?? '#d01137ff'
    );
    const response = await fetchChatResponse(
      this.url,
      this.sessionId,
      text,
      this.uniqueId ?? ''
    );
  }

  private resetFeedbackTimer() {
    if (this.feedbackTimer) {
      clearTimeout(this.feedbackTimer);
      this.feedbackTimer = null;
    }
    this.hideFeedback();
  }

  private startFeedbackTimer() {
    if (this.feedbackSubmitted) return;
    this.resetFeedbackTimer();
    this.feedbackTimer = setTimeout(() => {
      this.showFeedback();
    }, WitzoChatElement.FEEDBACK_IDLE_MS);
  }

  private showFeedback() {
    if (!this.chat || this.feedbackSubmitted) return;
    this.chat.feedbackBar.classList.remove('hidden');
  }

  private hideFeedback() {
    if (!this.chat) return;
    this.chat.feedbackBar.classList.add('hidden');
  }

  private async submitFeedback(type: 'like' | 'dislike') {
    if (!this.chat || !this.url || !this.uniqueId || this.feedbackSubmitted) return;

    this.feedbackSubmitted = true;
    if (this.feedbackTimer) {
      clearTimeout(this.feedbackTimer);
      this.feedbackTimer = null;
    }

    if (type === 'like') {
      this.chat.feedbackLikeBtn.classList.add('selected');
    } else {
      this.chat.feedbackDislikeBtn.classList.add('selected');
    }

    setTimeout(() => {
      if (!this.chat) return;
      this.chat.feedbackBar.innerHTML = '<p class="feedback-thanks">Thanks for your feedback.</p>';
      setTimeout(() => this.hideFeedback(), 2500);
    }, 300);

    try {
      const apiBaseUrl = new URL(this.url).origin + '/api/v1';
      await fetch(`${apiBaseUrl}/sessions/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          widget_key: this.uniqueId,
          session_id: this.sessionId,
          feedback: type,
        }),
      });
    } catch (err) {
      console.warn('[witzo-chat] Failed to submit feedback:', err);
    }
  }

  private clearGreetingNudgeTimer() {
    if (this.greetingNudgeTimer) {
      clearTimeout(this.greetingNudgeTimer);
      this.greetingNudgeTimer = null;
    }
  }

  private startHandoffPresenceTracking() {
    if (!this.handoffStatus) return;
    this.resetHandoffInactivityTimer();

    if (!this.handoffHeartbeatTimer) {
      this.handoffHeartbeatTimer = setInterval(() => {
        if (!this.handoffStatus) return;
        if (document.visibilityState === 'hidden' || (typeof navigator !== 'undefined' && navigator.onLine === false)) return;
        void this.syncHandoffPresence('active', 'heartbeat');
      }, WitzoChatElement.HANDOFF_HEARTBEAT_MS);
    }
  }

  private stopHandoffPresenceTracking() {
    if (this.handoffHeartbeatTimer) {
      clearInterval(this.handoffHeartbeatTimer);
      this.handoffHeartbeatTimer = null;
    }
    if (this.handoffInactiveTimer) {
      clearTimeout(this.handoffInactiveTimer);
      this.handoffInactiveTimer = null;
    }
    this.handoffMarkedLeft = false;
  }

  private resetHandoffInactivityTimer() {
    if (this.handoffInactiveTimer) {
      clearTimeout(this.handoffInactiveTimer);
      this.handoffInactiveTimer = null;
    }
    if (!this.handoffStatus) return;
    this.handoffInactiveTimer = setTimeout(() => {
      void this.markHandoffVisitorLeft('inactive_timeout');
    }, WitzoChatElement.HANDOFF_INACTIVE_MS);
  }

  private async markHandoffVisitorActive(reason = 'active', useBeacon = false) {
    if (!this.handoffStatus) return;
    this.handoffMarkedLeft = false;
    this.startHandoffPresenceTracking();
    await this.syncHandoffPresence('active', reason, useBeacon);
    this.startHandoffStream();
    this.scheduleHandoffPolling();
  }

  private async markHandoffVisitorLeft(reason = 'left', useBeacon = false) {
    if (!this.handoffStatus) return;
    this.handoffMarkedLeft = true;
    if (this.handoffInactiveTimer) {
      clearTimeout(this.handoffInactiveTimer);
      this.handoffInactiveTimer = null;
    }
    this.stopHandoffStream();
    this.stopHandoffPolling();
    await this.syncHandoffPresence('left', reason, useBeacon);
  }

  private stopHandoffPolling() {
    if (this.handoffPollTimer) {
      clearTimeout(this.handoffPollTimer);
      this.handoffPollTimer = null;
    }
  }

  private stopHandoffStream() {
    if (!this.handoffEventSource) return;
    this.handoffEventSource.close();
    this.handoffEventSource = null;
  }

  private startHandoffStream() {
    // No handoffStatus guard: the stream also runs in "watch" mode, before any
    // handoff exists, so an agent-initiated takeover reaches the visitor live.
    if (!this.url || !this.uniqueId || !this.sessionId || !this.handoffEnabled) return;
    if (typeof EventSource === 'undefined') return;

    this.stopHandoffStream();
    const apiBaseUrl = this.getResolvedApiBaseUrl();
    const streamUrl = `${apiBaseUrl}/chat/handoff/stream?widget_key=${encodeURIComponent(this.uniqueId)}&session_id=${encodeURIComponent(this.sessionId)}`;
    const eventSource = new EventSource(streamUrl);
    const refresh = () => {
      // In handoff mode, sync the conversation. Otherwise we are watching for a
      // takeover, so check whether one just opened.
      const task = this.handoffStatus
        ? this.pollHandoffStatus()
        : this.detectHandoffTakeover();
      void task.catch((error) => {
        console.warn('[witzo-chat] handoff stream refresh failed:', error);
      });
    };

    eventSource.addEventListener('message.created', refresh);
    eventSource.addEventListener('handoff.requested', refresh);
    eventSource.addEventListener('handoff.assigned', refresh);
    eventSource.addEventListener('handoff.closed', refresh);
    eventSource.addEventListener('handoff.updated', refresh);
    eventSource.onerror = () => {
      eventSource.close();
      if (this.handoffEventSource === eventSource) {
        this.handoffEventSource = null;
        if (this.handoffStatus) {
          this.scheduleHandoffPolling();
        } else {
          this.scheduleHandoffWatchPoll();
        }
      }
    };

    this.handoffEventSource = eventSource;
  }

  /**
   * Watches a plain AI conversation for an agent taking it over from the
   * dashboard. Without this the visitor would not learn about the takeover until
   * they happened to send another message.
   */
  private startHandoffWatch() {
    if (!this.handoffEnabled || this.handoffStatus) return;
    // Don't hold a connection open for every page view. A visitor who never
    // opened the chat has no server-side session to take over, so there is
    // nothing to watch for until they engage.
    if (!this.hasOpenedWidget) return;
    if (!this.url || !this.uniqueId || !this.sessionId) return;
    // Already watching — don't churn the stream on repeated opens.
    if (this.handoffEventSource || this.handoffWatchTimer) return;

    this.startHandoffStream();
    // Only fall back to polling when the stream could not be opened.
    if (!this.handoffEventSource) this.scheduleHandoffWatchPoll();
  }

  private stopHandoffWatch() {
    if (!this.handoffWatchTimer) return;
    clearTimeout(this.handoffWatchTimer);
    this.handoffWatchTimer = null;
  }

  private scheduleHandoffWatchPoll() {
    this.stopHandoffWatch();
    if (!this.handoffEnabled || this.handoffStatus) return;

    this.handoffWatchTimer = setTimeout(() => {
      // No .finally() — the widget targets a pre-ES2018 lib.
      const reschedule = () => this.scheduleHandoffWatchPoll();
      this.detectHandoffTakeover().then(reschedule, (error) => {
        console.warn('[witzo-chat] handoff watch failed:', error);
        reschedule();
      });
    }, WitzoChatElement.HANDOFF_WATCH_POLL_MS);
  }

  private async detectHandoffTakeover() {
    if (!this.url || !this.uniqueId || !this.sessionId || !this.chat) return;
    if (!this.handoffEnabled || this.handoffStatus || this.handoffRestoring) return;

    const apiBaseUrl = this.getResolvedApiBaseUrl();
    const status = await fetchTextHandoffStatus(
      apiBaseUrl,
      this.uniqueId,
      this.sessionId,
      getStoredHandoffId()
    );

    if (!status.active || !status.handoff) return;

    // An agent pulled this chat into a human handoff; switch modes the same way
    // the visitor-initiated flow does.
    this.stopHandoffWatch();
    storeActiveHandoffSession(this.sessionId, status.handoff.id);
    this.handoffStatus = status.handoff.status;
    this.handoffAssignedNotified = status.handoff.status === 'assigned';
    this.setHandoffButtonState(
      status.handoff.status === 'assigned' ? 'assigned' : 'waiting'
    );
    appendHandoffNotification(this.chat.messagesContainer, status.handoff.output);
    await this.markHandoffVisitorActive('agent_takeover');
    await this.pollHandoffStatus();
  }

  private clearHandoffWaitingIndicator() {
    if (this.handoffWaitingTimer) {
      clearTimeout(this.handoffWaitingTimer);
      this.handoffWaitingTimer = null;
    }
    if (!this.handoffWaitingWrapper) return;
    this.handoffWaitingWrapper.remove();
    this.handoffWaitingWrapper = null;
  }

  private ensureHandoffWaitingIndicator(wrapper?: HTMLDivElement, delayMs = 0) {
    if (!this.chat || !this.handoffAwaitingAgentReply) return;

    const showIndicator = () => {
      if (!this.chat || !this.handoffAwaitingAgentReply) return;
      if (wrapper) {
        if (this.handoffWaitingWrapper && this.handoffWaitingWrapper !== wrapper) {
          this.handoffWaitingWrapper.remove();
        }
        this.handoffWaitingWrapper = wrapper;
      } else if (!this.handoffWaitingWrapper) {
        this.handoffWaitingWrapper = showDots(
          this.chat.messagesContainer,
          this.bot_logo_color ?? '#fc0e3f'
        );
      }

      this.chat.messagesContainer.scrollTop = this.chat.messagesContainer.scrollHeight;
    };

    if (this.handoffWaitingTimer) {
      clearTimeout(this.handoffWaitingTimer);
      this.handoffWaitingTimer = null;
    }

    if (delayMs > 0) {
      this.handoffWaitingTimer = setTimeout(() => {
        this.handoffWaitingTimer = null;
        showIndicator();
      }, delayMs);
      return;
    }

    showIndicator();
  }

  private scheduleHandoffPolling() {
    this.stopHandoffPolling();
    if (!this.handoffEnabled || !this.handoffStatus || !this.uniqueId || !this.url) return;
    if (this.handoffEventSource !== null) return;

    this.handoffPollTimer = setTimeout(() => {
      this.pollHandoffStatus().catch((error) => {
        console.warn('[witzo-chat] handoff polling failed:', error);
      });
    }, WitzoChatElement.HANDOFF_POLL_MS);
  }

  private async pollHandoffStatus() {
    if (!this.url || !this.uniqueId || !this.chat || !this.sessionId || !this.handoffStatus) return;

    const apiBaseUrl = this.getResolvedApiBaseUrl();
    const status = await fetchTextHandoffStatus(apiBaseUrl, this.uniqueId, this.sessionId, getStoredHandoffId());

    if (!status.active || !status.handoff) {
      const closedMessage = (status as any).closed_message as string | undefined;
      if (closedMessage && this.chat) {
        const wrapper = getPrimaryMessageWrapper(
          this.chat.messagesContainer,
          this.bot_logo_color ?? '#fc0e3f'
        );
        recieveAndAppendMessage(
          wrapper,
          this.chat.messagesContainer,
          Promise.resolve({ content: closedMessage })
        );
      }
      clearHandoffId();
      this.handoffStatus = null;
      this.handoffAssignedNotified = false;
      this.handoffAwaitingAgentReply = false;
      this.clearHandoffWaitingIndicator();
      this.setHandoffButtonState('idle');
      this.stopHandoffPolling();
      this.stopHandoffStream();
      this.stopHandoffPresenceTracking();
      // Back to AI mode — resume watching in case an agent takes over later.
      this.startHandoffWatch();
      return;
    }

    storeActiveHandoffSession(this.sessionId, status.handoff.id);
    const previousStatus = this.handoffStatus;
    this.handoffStatus = status.handoff.status;
    this.startHandoffPresenceTracking();
    this.startHandoffStream();
    this.setHandoffButtonState(status.handoff.status === 'assigned' ? 'assigned' : 'waiting');

    if (status.handoff.status === 'assigned' && previousStatus !== 'assigned' && !this.handoffAssignedNotified) {
      this.handoffAssignedNotified = true;
      appendHandoffNotification(this.chat.messagesContainer, status.handoff.output);
    }

    this.handoffAwaitingAgentReply = Boolean(status.awaiting_agent_reply);
    this.clearHandoffWaitingIndicator();
    // Handoff typing indicator disabled: when a human agent is connected,
    // don't show the "..." waiting bubble while the visitor is chatting.
    // if (!this.handoffAwaitingAgentReply) {
    //   this.clearHandoffWaitingIndicator();
    // } else {
    //   this.ensureHandoffWaitingIndicator(
    //     undefined,
    //     Math.max(0, Number(status.wait_remaining_ms || 0))
    //   );
    // }

    const newMessages = status.messages.filter((message) => !this.seenHandoffMessageIds.has(message.id));

    for (const message of newMessages) {
      this.seenHandoffMessageIds.add(message.id);

      const wrapper = getPrimaryMessageWrapper(
        this.chat.messagesContainer,
        this.bot_logo_color ?? '#fc0e3f'
      );
      recieveAndAppendMessage(
        wrapper,
        this.chat.messagesContainer,
        Promise.resolve({ content: message.content })
      );
    }

    this.scheduleHandoffPolling();
  }

  private async restoreExistingHandoff() {
    if (!this.url || !this.uniqueId || !this.sessionId || !this.chat || !this.handoffEnabled) return;

    this.handoffRestoring = true;
    try {
      const apiBaseUrl = this.getResolvedApiBaseUrl();
      const storedHandoffId = getStoredHandoffId();
      const status = await fetchTextHandoffStatus(apiBaseUrl, this.uniqueId, this.sessionId, storedHandoffId);
      if (!status.active || !status.handoff) {
        clearHandoffId();
        return;
      }

      storeActiveHandoffSession(this.sessionId, status.handoff.id);
      this.handoffStatus = status.handoff.status;
      this.handoffAssignedNotified = status.handoff.status === 'assigned';
      this.setHandoffButtonState(status.handoff.status === 'assigned' ? 'assigned' : 'waiting');
      await this.markHandoffVisitorActive('restored');
      await this.pollHandoffStatus();
    } catch (error) {
      console.warn('[witzo-chat] Failed to restore handoff:', error);
    } finally {
      this.handoffRestoring = false;
    }
  }

  private async requestHumanHandoff() {
    if (!this.url || !this.uniqueId || !this.chat || !this.handoffEnabled) return;

    try {
      const apiBaseUrl = this.getResolvedApiBaseUrl();
      this.handoffAssignedNotified = false;
      this.setHandoffButtonState('requesting');

      const response = await requestTextHandoff(apiBaseUrl, this.uniqueId, this.sessionId);
      this.handoffStatus = response.handoff?.status ?? 'waiting_for_agent';
      if (response.handoff?.id) storeActiveHandoffSession(this.sessionId, response.handoff.id);
      appendHandoffNotification(this.chat.messagesContainer, response.content);
      await this.markHandoffVisitorActive('handoff_requested');
      this.setHandoffButtonState('waiting');
      await this.pollHandoffStatus();
    } catch (error) {
      console.warn('[witzo-chat] Failed to request human handoff:', error);
      this.setHandoffButtonState('idle');
      appendHandoffNotification(
        this.chat.messagesContainer,
        error instanceof Error ? error.message : 'Unable to connect you to a human agent right now.'
      );
    }
  }

  private setHandoffButtonState(state: 'idle' | 'requesting' | 'waiting' | 'assigned') {
    if (!this.chat?.handoffButton || !this.chat?.handoffButtonWrap) return;

    const tooltip = this.chat.handoffButtonWrap.querySelector('.handoff-tooltip');
    const tooltipText =
      state === 'assigned'
        ? 'Agent connected'
        : state === 'waiting' || state === 'requesting'
          ? 'Waiting for agent'
          : 'Talk to human';

    this.chat.handoffButton.disabled = state !== 'idle';
    this.chat.handoffButton.setAttribute('data-state', state);
    this.chat.handoffButton.setAttribute('aria-label', tooltipText);
    if (tooltip) tooltip.textContent = tooltipText;
  }

  private async syncHandoffPresence(state: 'active' | 'left', reason = '', useBeacon = false) {
    if (!this.url || !this.uniqueId || !this.sessionId || !this.handoffStatus) return;

    const apiBaseUrl = this.getResolvedApiBaseUrl();
    try {
      await notifyTextHandoffPresence(apiBaseUrl, this.uniqueId, this.sessionId, state, reason, useBeacon);
    } catch (error) {
      console.warn('[witzo-chat] Failed to sync handoff presence:', error);
    }
  }

  private isGreetingOnlyMessage(text: string): boolean {
    const normalized = text
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (!normalized) return false;

    const greetingPhrases = new Set([
      'hi',
      'hii',
      'hiii',
      'hey',
      'heyy',
      'heyyy',
      'hello',
      'yo',
      'hola',
      'good morning',
      'good afternoon',
      'good evening',
    ]);

    return greetingPhrases.has(normalized);
  }

  private scheduleGreetingNudge(userMessageId: number) {
    if (!this.chat) return;
    this.clearGreetingNudgeTimer();

    this.greetingNudgeTimer = setTimeout(() => {
      if (!this.chat) return;
      if (userMessageId !== this.latestUserMessageId) return;

      const wrapper = getPrimaryMessageWrapper(
        this.chat.messagesContainer,
        this.bot_logo_color ?? '#fc0e3f'
      );
      wrapper.style.marginTop = '8px';
      recieveAndAppendMessage(
        wrapper,
        this.chat.messagesContainer,
        Promise.resolve({
          content: 'You there? Please tell me what you want to know, and I will assist you.',
        })
      );
    }, WitzoChatElement.GREETING_NUDGE_MS);
  }

  private detectConversationEndIntent(text: string): boolean {
    const normalized = text
      .trim()
      .toLowerCase()
      .replace(/[’']/g, "'")
      .replace(/[^a-z0-9'\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (!normalized) return false;

    const endPhrases = [
      'no thanks',
      'i am done',
      "i'm done",
      "that's all",
      'bye',
      'thank you',
      'thanks',
      'okay thanks',
      'ok thanks',
      'no thank you',
    ];

    return endPhrases.some((phrase) => normalized.includes(phrase));
  }

  async connectedCallback() {
    this.bindEvents();

    // Initialize session ID
    this.sessionId = createToken();
    this.feedbackSubmitted = false;
    this.resetFeedbackTimer();

    // Initialize Calendly
    await this.initializeCalendly();

    // Process any pending default text after connection and initialization
    if (this.isInitialized && this.default_primary_text) {
      this.processAndDisplayMessage();
    }

    setTimeout(() => {
      const wasClosedByUser = localStorage.getItem('witzo_widget_closed') === 'true';
      if (
        this.flags.isAutoOpen === true &&
        this.flags.isButtonClicked === false &&
        !wasClosedByUser
      ) {
        this.toggleButton();
      }
    }, 5000);

    setTimeout(() => {
      this.chat?.floatingButton.classList.remove('hidden');
    }, 2000);

    window.addEventListener('pagehide', this.handoffPresenceHandler);
    window.addEventListener('beforeunload', this.handoffPresenceHandler);
    document.addEventListener('visibilitychange', this.handoffVisibilityHandler);
    window.addEventListener('online', this.handoffOnlineHandler);
    window.addEventListener('offline', this.handoffOfflineHandler);
    window.addEventListener('storage', this.activeHandoffSessionHandler);
    window.addEventListener('witzo-active-handoff-session-change', this.activeHandoffSessionHandler);
    ['click', 'keydown', 'mousemove', 'touchstart'].forEach((eventName) => {
      window.addEventListener(eventName, this.handoffActivityHandler, { passive: true });
    });
  }

  disconnectedCallback() {
    void this.markHandoffVisitorLeft('component_disconnected', true);
    window.removeEventListener('pagehide', this.handoffPresenceHandler);
    window.removeEventListener('beforeunload', this.handoffPresenceHandler);
    document.removeEventListener('visibilitychange', this.handoffVisibilityHandler);
    window.removeEventListener('online', this.handoffOnlineHandler);
    window.removeEventListener('offline', this.handoffOfflineHandler);
    window.removeEventListener('storage', this.activeHandoffSessionHandler);
    window.removeEventListener('witzo-active-handoff-session-change', this.activeHandoffSessionHandler);
    ['click', 'keydown', 'mousemove', 'touchstart'].forEach((eventName) => {
      window.removeEventListener(eventName, this.handoffActivityHandler);
    });
    this.stopHandoffPolling();
    this.stopHandoffWatch();
    this.stopHandoffStream();
    this.stopHandoffPresenceTracking();
    this.handoffAwaitingAgentReply = false;
    this.clearHandoffWaitingIndicator();
    this.clearGreetingNudgeTimer();
    this.resetFeedbackTimer();
  }

  bindEvents() {
    if (!this.chat || this.isInitialized === false) return;
    const {
      // backButton,
      closeButton,
      sendButton,
      messageInput,
      messagesContainer,
      chatWindow,
      floatingButton,
      calendlyButton,
      calendlyCloseButton,
      handoffButton,
      feedbackLikeBtn,
      feedbackDislikeBtn,
    } = this.chat;

    floatingButton.addEventListener('click', () => {
      this.toggleButton();
    });

    // backButton.addEventListener('click', () => {
    //   this._mainWindow.back();
    //   this.chat?.chatWindow.classList.add('hidden');
    // });

    closeButton.addEventListener('click', () => {
      void this.markHandoffVisitorLeft('widget_closed');
      this._mainWindow.close();
      this.chat?.chatWindow.classList.add('hidden');
      this.flags.isOpen = false;
      // Remember that user closed the widget so it won't auto-open again
      localStorage.setItem('witzo_widget_closed', 'true');
    });

    // Calendly button handlers
    calendlyButton.addEventListener('click', () => {
      this.openCalendly();
    });

    calendlyCloseButton.addEventListener('click', () => {
      this.closeCalendly();
    });
    handoffButton.addEventListener('click', () => {
      this.requestHumanHandoff();
    });
    feedbackLikeBtn.addEventListener('click', () => {
      this.submitFeedback('like');
    });
    feedbackDislikeBtn.addEventListener('click', () => {
      this.submitFeedback('dislike');
    });

    const gapRequired = 2 * 60 * 1000;
    sendButton.addEventListener('click', async () => {
      if (!this.url) return;
      const text = this.chat!.messageInput.value.trim();
      const isGreetingOnly = this.isGreetingOnlyMessage(text);
      const userMessageId = ++this.latestUserMessageId;
      const shouldPromptFeedbackAfterReply = this.detectConversationEndIntent(text);
      const requestId = ++this.latestRequestId;

      const gap = new Date().getTime() - this.date.getTime();
      if (gap > gapRequired && !this.handoffStatus && !this.handoffRestoring) {
        this.successfulChatCount = 0;
        this.date = new Date();
        localStorage.setItem('witzo_chat_date', this.date.toISOString());
      }

      if (text !== '') {
        if (this.handoffStatus) {
          void this.markHandoffVisitorActive('user_message');
        }
        this.clearGreetingNudgeTimer();
        this.resetFeedbackTimer();
        sendAndAppendMessage(
          messagesContainer,
          messageInput,
          text,
          this.user_chat_color ?? '#d01137ff'
        );

        // Check if user wants to book a meeting
        const hasBookingIntent = detectBookingIntent(text);

        if (hasBookingIntent && this.calendlyUrl && this.chat) {
          // Show loading message
          showCalendlyLoadingMessage(messagesContainer);

          // Wait a bit for better UX
          await new Promise((resolve) => setTimeout(resolve, 500));

          // Show inline Calendly widget with widget expansion
          const widgetElement = this.chat.chatWindow;

          if (widgetElement) {
            // Define skip handler to restore chat
            const handleSkip = () => {
              // Collapse widget and restore chat
              collapseWidget(widgetElement, messagesContainer);
              this.setWidgetLayout('default');
              restoreChatMessages(messagesContainer);
            };

            this.setWidgetLayout('calendar');
            appendInlineCalendlyWidget(
              messagesContainer,
              this.calendlyUrl,
              widgetElement,
              handleSkip
            );
          } else {
            console.error('[Booking] Widget element not found');
          }

          return; // Don't send to webhook
        }

        // During human handoff, don't show the AI/loading "..." bubble after a visitor sends a message.
        const wrapper = this.handoffStatus
          ? null
          : showDots(messagesContainer, this.bot_logo_color ?? '#fc0e3f');
        let responsePromise = fetchChatResponse(
          this.url,
          this.sessionId,
          text,
          this.uniqueId ?? '',
          this.successfulChatCount
        );

        this.successfulChatCount += 1;
        sessionStorage.setItem('witzo_chat_count', `${this.successfulChatCount}`);

        let response = await responsePromise;
        if (!this.handoffStatus && !this.handoffRestoring && response.content?.includes('You have exceeded your Chat limit')) {
          this.sessionId = resetToken();
          response = await fetchChatResponse(
            this.url,
            this.sessionId,
            text,
            this.uniqueId ?? '',
            this.successfulChatCount
          );
        }
        if (requestId !== this.latestRequestId) {
          wrapper?.remove();
          return;
        }

        if (response.handoff?.active) {
          this.handoffStatus = response.handoff.status;
          wrapper?.remove();
          this.setHandoffButtonState(
            response.handoff.status === 'assigned' ? 'assigned' : 'waiting'
          );
          await this.pollHandoffStatus();
        } else {
          this.handoffAwaitingAgentReply = false;
          this.clearHandoffWaitingIndicator();
          const responseWrapper = wrapper ?? showDots(messagesContainer, this.bot_logo_color ?? '#fc0e3f');
          await recieveAndAppendMessage(responseWrapper, messagesContainer, Promise.resolve(response));
        }

        if (isGreetingOnly) {
          this.scheduleGreetingNudge(userMessageId);
        }

        if (shouldPromptFeedbackAfterReply) {
          this.showFeedback();
        } else {
          this.startFeedbackTimer();
        }
      }
    });

    messageInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        sendButton.click();
      }
    });
  }

  /**
   * @description toggle button for floating button
   */
  toggleButton() {
    if (!this.chat) return;
    this.flags.isButtonClicked = true;
    this.flags.isOpen = !this.flags.isOpen;
    this.flags.isOpen ? this.maximizeWidget() : this.minimizeWidget();
  }

  private setWidgetLayout(mode: 'default' | 'chat' | 'calendar') {
    if (!this.chat) return;

    this.chat.chatWindow.classList.toggle('chat-expanded', mode === 'chat');
    this.chat.chatWindow.classList.toggle('calendar-expanded', mode === 'calendar');

    this.dispatchEvent(
      new CustomEvent('witzo-widget-layout-change', {
        detail: { mode },
        bubbles: true,
        composed: true,
      })
    );
  }

  /**Minimises the chat window */
  minimizeWidget() {
    if (!this.chat) return;

    // Remember that user closed the widget so it won't auto-open again
    localStorage.setItem('witzo_widget_closed', 'true');
    this.closeCalendly();

    this.chat.chatWindow.classList.add('minimizing');
    setTimeout(() => {
      this.setWidgetLayout('default');
      this.chat?.chatWindow.classList.add('hidden');
      this.chat?.chatWindow.classList.remove('minimizing');
      //this.chat?.openButton.classList.remove('hidden');
    }, 300);
  }

  /**Maximize the chat window */
  maximizeWidget() {
    if (!this.chat) return;

    //this.chat.openButton.classList.add('hidden');
    this.chat.chatWindow.classList.remove('hidden');
    this.setWidgetLayout('default');
    this.chat.messageInput.focus();
    this.hasOpenedWidget = true;
    if (this.handoffStatus) {
      void this.markHandoffVisitorActive('widget_opened');
    } else {
      // Watch for an agent taking this chat over from the dashboard.
      this.startHandoffWatch();
    }

    if (!this.url || !this.uniqueId) return;
    void this.initializeCalendly();
  }

  /**
   * Initialize Calendly integration
   */
  private async initializeCalendly() {
    if (!this.url || !this.uniqueId) return;
    if (this.calendlyInitialized) return;
    // Claim the guard synchronously: connectedCallback and the chatUrl/uniqueID/
    // apiBaseURL setters can all call this concurrently, and awaiting before
    // claiming lets them all through and attaches duplicate message listeners.
    this.calendlyInitialized = true;

    try {
      // Extract API base URL from webhook URL
      const apiBaseUrl = this.getResolvedApiBaseUrl();

      // Fetch widget configuration
      const config = await fetchWidgetConfig(apiBaseUrl, this.uniqueId);

      if (config.has_calendly && config.calendly_url) {
        this.calendlyUrl = config.calendly_url;

        // Show Calendly button
        this.chat?.calendlyButtonWrap.classList.remove('hidden');

        await loadCalendlyScript();

        // Setup Calendly event listener
        this.setupCalendlyEventListener();
      }

      this.handoffEnabled = Boolean(config.text_handoff_enabled);
      if (this.handoffEnabled && this.chat?.handoffButtonWrap) {
        this.chat.handoffButtonWrap.classList.remove('hidden');
      }
      await this.restoreExistingHandoff();
      // Nothing in progress: watch for an agent taking this chat over.
      if (!this.handoffStatus) this.startHandoffWatch();
    } catch (error) {
      // Release the guard so a later call can retry a failed init.
      this.calendlyInitialized = false;
      console.error('Failed to initialize Calendly:', error);
    }
  }

  /**
   * Open Calendly booking interface
   */
  private openCalendly() {
    if (!this.calendlyUrl || !this.chat) return;

    this.setWidgetLayout('calendar');

    // Show Calendly container
    this.chat.calendlyContainer.classList.add('active');

    // Clear previous widget
    this.chat.calendlyWidgetWrapper.innerHTML = '';

    // Initialize Calendly inline widget
    initCalendlyInline(this.chat.calendlyWidgetWrapper, this.calendlyUrl);
  }

  /**
   * Close Calendly booking interface
   */
  private closeCalendly() {
    if (!this.chat) return;

    this.chat.calendlyContainer.classList.remove('active');
    this.setWidgetLayout('default');
  }

  /**
   * Setup Calendly event listeners for tracking bookings
   */
  private setupCalendlyEventListener() {
    if (this.calendlyListenerAttached) return;
    this.calendlyListenerAttached = true;

    window.addEventListener('message', (e: MessageEvent) => {
      if (e.origin !== 'https://calendly.com') return;

      const data: CalendlyEventData = e.data;

      // Event scheduled successfully
      if (data.event === 'calendly.event_scheduled') {
        this.handleCalendlyEventScheduled(data);
      }
    });
  }

  /**
   * Handle Calendly event scheduled
   */
  private async handleCalendlyEventScheduled(data: CalendlyEventData) {
    if (!this.chat || !this.url || !this.uniqueId) return;

    // Calendly can emit event_scheduled more than once for a single booking.
    const eventUri = data.payload?.event?.uri || '';
    if (eventUri) {
      if (this.loggedEventUris.has(eventUri)) return;
      this.loggedEventUris.add(eventUri);
    }

    // Close Calendly interface
    this.closeCalendly();

    // Collapse widget back to normal size and restore chat
    const widgetElement = this.chat.chatWindow;
    if (widgetElement) {
      collapseWidget(widgetElement, this.chat.messagesContainer);
      this.setWidgetLayout('default');
      restoreChatMessages(this.chat.messagesContainer);
    }

    // Show confirmation message in chat
    const confirmationMessage =
      'Meeting booked successfully! You will receive a confirmation email shortly.';
    const wrapper = getPrimaryMessageWrapper(
      this.chat.messagesContainer,
      this.bot_logo_color ?? '#fc0e3f'
    );
    recieveAndAppendMessage(
      wrapper,
      this.chat.messagesContainer,
      Promise.resolve({ content: confirmationMessage })
    );

    // Log meeting to backend
    try {
      const apiBaseUrl = this.getResolvedApiBaseUrl();
      const inviteeUri = data.payload?.invitee?.uri || '';

      await logMeetingBooking(apiBaseUrl, {
        session_id: this.sessionId,
        widget_key: this.uniqueId,
        event_uri: eventUri,
        invitee_uri: inviteeUri,
      });
    } catch (error) {
      console.error('Failed to log meeting:', error);
    }
  }
}

export { WitzoChatElement };
